import { FC, useState } from "react";
import {
  Modal,
  Descriptions,
  Tag,
  Space,
  Tabs,
  Card,
  Spin,
  Empty,
  Table,
  Button,
  Upload,
  Select,
  DatePicker,
  message,
  Popconfirm,
  Form,
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  StarOutlined,
  TrophyOutlined,
  DollarOutlined,
  FileOutlined,
  UploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useGetStaffById } from "~/services/operation";
import { useGetFiles, useCreateFile, useDeleteFile, useUpdateFile } from "~/services/file";
import { formatStaffName } from "~/utils/format-staff-name";
import { capitalize } from "~/utils";
import { useAuth } from "~/store";
import { Role } from "~/utils/constants";
import dayjs from "dayjs";
import { StarRating } from "~/components/common/star-rating";
import { DocumentType, EmploymentType, STAFF_DOCUMENT_TYPES } from "~/utils/constants";
import { useQueryClient } from "@tanstack/react-query";
import { FILE_KEYS } from "~/services/file/file.keys";
import { TFile } from "~/types";

interface Props {
  staffId: string | null;
  visible: boolean;
  onClose: () => void;
}

export const StaffProfileView: FC<Props> = ({ staffId, visible, onClose }) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === Role.SUPER_ADMIN;
  const queryClient = useQueryClient();
  const [uploadDocType, setUploadDocType] = useState<DocumentType>(DocumentType.OTHER);
  const [uploadExpiry, setUploadExpiry] = useState<dayjs.Dayjs | null>(null);
  const [uploading, setUploading] = useState(false);
  const [editingFile, setEditingFile] = useState<TFile | null>(null);
  const [editForm] = Form.useForm();

  const { data: staff, isLoading } = useGetStaffById(
    staffId || "",
    !!staffId && visible
  );

  const { data: filesData, isLoading: loadingFiles } = useGetFiles(
    { staffId: staffId || "", page: 1, pageSize: 100 },
    !!staffId && visible
  );

  const createFileMutation = useCreateFile();
  const deleteFileMutation = useDeleteFile();
  const updateFileMutation = useUpdateFile();

  const handleUpload = async (file: File) => {
    if (!staffId) return;
    setUploading(true);
    try {
      await createFileMutation.mutateAsync({
        title: file.name,
        file,
        staffId,
        type: uploadDocType,
        expires: uploadExpiry?.toISOString(),
      });
      message.success("Document uploaded successfully");
      queryClient.invalidateQueries({ queryKey: FILE_KEYS.lists() });
      setUploadExpiry(null);
    } catch (error: any) {
      console.error("File upload error:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to upload document";
      message.error(Array.isArray(errorMessage) ? errorMessage.join(", ") : errorMessage);
    } finally {
      setUploading(false);
    }
    return false; // Prevent default upload behavior
  };

  const handleDelete = async (fileId: string) => {
    try {
      await deleteFileMutation.mutateAsync(fileId);
      message.success("Document deleted");
      queryClient.invalidateQueries({ queryKey: FILE_KEYS.lists() });
    } catch (error) {
      message.error("Failed to delete document");
    }
  };

  const handleEditFile = (file: TFile) => {
    setEditingFile(file);
    editForm.setFieldsValue({
      type: file.type,
      expires: file.expires ? dayjs(file.expires) : null,
    });
  };

  const handleUpdateFile = async () => {
    if (!editingFile) return;
    try {
      const values = await editForm.validateFields();
      await updateFileMutation.mutateAsync({
        id: editingFile.id,
        type: values.type,
        expires: values.expires?.toISOString(),
      });
      message.success("Document updated");
      queryClient.invalidateQueries({ queryKey: FILE_KEYS.lists() });
      setEditingFile(null);
      editForm.resetFields();
    } catch (error) {
      message.error("Failed to update document");
    }
  };

  const getDocumentTypeLabel = (type: DocumentType) => {
    const found = STAFF_DOCUMENT_TYPES.find((t) => t.value === type);
    return found?.label || type;
  };

  if (!staffId) return null;

  return (
    <Modal
      title={
        <Space>
          <UserOutlined />
          <span>Staff Profile</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      centered
    >
      <Spin spinning={isLoading}>
        {staff ? (
          <Tabs
            defaultActiveKey="profile"
            items={[
              {
                key: "profile",
                label: (
                  <span>
                    <UserOutlined />
                    Profile Information
                  </span>
                ),
                children: (
                  <Card>
                    <Descriptions
                      bordered
                      column={1}
                      labelStyle={{ fontWeight: 500, width: "200px" }}
                    >
                      <Descriptions.Item
                        label={
                          <Space>
                            <UserOutlined />
                            Name
                          </Space>
                        }
                      >
                        {formatStaffName(staff)}
                      </Descriptions.Item>

                      <Descriptions.Item
                        label={
                          <Space>
                            <UserOutlined />
                            Username
                          </Space>
                        }
                      >
                        {staff.user?.username || "-"}
                      </Descriptions.Item>

                      <Descriptions.Item
                        label={
                          <Space>
                            <MailOutlined />
                            Email
                          </Space>
                        }
                      >
                        {staff.user?.email || "-"}
                      </Descriptions.Item>

                      <Descriptions.Item
                        label={
                          <Space>
                            <PhoneOutlined />
                            Phone
                          </Space>
                        }
                      >
                        {staff.user?.phoneNumber || "-"}
                      </Descriptions.Item>

                      <Descriptions.Item
                        label={
                          <Space>
                            <TrophyOutlined />
                            Position
                          </Space>
                        }
                      >
                        {staff.position || "-"}
                      </Descriptions.Item>

                      <Descriptions.Item label="Role Tags">
                        <Space>
                          {staff.lead && <Tag color="purple">Lead</Tag>}
                          {staff.driver && <Tag color="blue">Driver</Tag>}
                          {!staff.lead && !staff.driver && <Tag>Staff</Tag>}
                        </Space>
                      </Descriptions.Item>

                      <Descriptions.Item
                        label={
                          <Space>
                            <DollarOutlined />
                            Employment Type
                          </Space>
                        }
                      >
                        <Tag
                          color={
                            staff.employmentType === EmploymentType.W2
                              ? "green"
                              : "blue"
                          }
                        >
                          {staff.employmentType === EmploymentType.W2
                            ? "W2 Employee"
                            : "1099 Contractor"}
                        </Tag>
                      </Descriptions.Item>

                      <Descriptions.Item label="Staff Roles">
                        {staff.staffRoleAssignments &&
                        staff.staffRoleAssignments.length > 0 ? (
                          <Space direction="vertical" size="small" style={{ width: "100%" }}>
                            {staff.staffRoleAssignments.map((assignment) => (
                              <div
                                key={assignment.id}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  padding: "4px 8px",
                                  background: "#f5f5f5",
                                  borderRadius: 4,
                                }}
                              >
                                <Tag color={assignment.isPrimary ? "gold" : "default"}>
                                  {assignment.staffRole?.name || "Unknown Role"}
                                  {assignment.isPrimary && " (Primary)"}
                                </Tag>
                                {isSuperAdmin && assignment.customHourlyCost && (
                                  <span style={{ fontSize: 12, color: "#666" }}>
                                    Custom Cost: ${assignment.customHourlyCost}/hr
                                  </span>
                                )}
                              </div>
                            ))}
                          </Space>
                        ) : staff.staffRole ? (
                          <Tag>{staff.staffRole.name}</Tag>
                        ) : (
                          <span style={{ color: "#999" }}>No roles assigned</span>
                        )}
                      </Descriptions.Item>

                      <Descriptions.Item
                        label={
                          <Space>
                            <StarOutlined />
                            Level
                          </Space>
                        }
                      >
                        {(() => {
                          const installation = staff.installation ?? 0;
                          const packing = staff.packing ?? 0;
                          const handling = staff.handling ?? 0;
                          const clientRelations = staff.clientRelations ?? 0;
                          const teamwork = staff.teamwork ?? 0;
                          const overallLevel =
                            (installation +
                              packing +
                              handling +
                              clientRelations +
                              teamwork) /
                            5;

                          const hasSkills =
                            installation > 0 ||
                            packing > 0 ||
                            handling > 0 ||
                            clientRelations > 0 ||
                            teamwork > 0;

                          if (!hasSkills) {
                            return (
                              <span style={{ color: "#999" }}>
                                No level set yet
                              </span>
                            );
                          }

                          return (
                            <div>
                              <div style={{ marginBottom: "12px" }}>
                                <Space>
                                  <span style={{ fontWeight: 600 }}>
                                    Overall: {overallLevel.toFixed(1)}
                                  </span>
                                  <StarRating rating={overallLevel} />
                                </Space>
                              </div>
                              <div style={{ fontSize: "12px" }}>
                                <Space direction="vertical" size="small">
                                  {installation > 0 && (
                                    <Space>
                                      <span style={{ width: "120px", display: "inline-block" }}>
                                        Installation:
                                      </span>
                                      <StarRating rating={installation} />
                                      <span>({installation})</span>
                                    </Space>
                                  )}
                                  {packing > 0 && (
                                    <Space>
                                      <span style={{ width: "120px", display: "inline-block" }}>
                                        Packing:
                                      </span>
                                      <StarRating rating={packing} />
                                      <span>({packing})</span>
                                    </Space>
                                  )}
                                  {handling > 0 && (
                                    <Space>
                                      <span style={{ width: "120px", display: "inline-block" }}>
                                        Handling:
                                      </span>
                                      <StarRating rating={handling} />
                                      <span>({handling})</span>
                                    </Space>
                                  )}
                                  {clientRelations > 0 && (
                                    <Space>
                                      <span style={{ width: "120px", display: "inline-block" }}>
                                        Client Relations:
                                      </span>
                                      <StarRating rating={clientRelations} />
                                      <span>({clientRelations})</span>
                                    </Space>
                                  )}
                                  {teamwork > 0 && (
                                    <Space>
                                      <span style={{ width: "120px", display: "inline-block" }}>
                                        Teamwork:
                                      </span>
                                      <StarRating rating={teamwork} />
                                      <span>({teamwork})</span>
                                    </Space>
                                  )}
                                </Space>
                              </div>
                            </div>
                          );
                        })()}
                      </Descriptions.Item>

                      <Descriptions.Item label="Skills & Certifications">
                        {staff.skills && staff.skills.length > 0 ? (
                          <Space wrap>
                            {staff.skills.map((skill, index) => (
                              <Tag
                                key={index}
                                color={
                                  skill.includes("Forklift") ||
                                  skill.includes("OSHA")
                                    ? "green"
                                    : "blue"
                                }
                              >
                                {skill}
                              </Tag>
                            ))}
                          </Space>
                        ) : (
                          <span style={{ color: "#999" }}>No skills listed</span>
                        )}
                      </Descriptions.Item>

                      <Descriptions.Item label="Status">
                        {staff.status ? (
                          <Tag
                            color={
                              staff.status === "ACTIVE"
                                ? "green"
                                : staff.status === "HIATUS"
                                ? "orange"
                                : "red"
                            }
                          >
                            {capitalize(staff.status)}
                          </Tag>
                        ) : (
                          <Tag color="green">Active</Tag>
                        )}
                      </Descriptions.Item>

                      <Descriptions.Item
                        label={
                          <Space>
                            <CalendarOutlined />
                            Added Date
                          </Space>
                        }
                      >
                        {staff.createdAt
                          ? dayjs(staff.createdAt).format("MMMM D, YYYY")
                          : "-"}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                ),
              },
              {
                key: "documents",
                label: (
                  <span>
                    <FileOutlined />
                    Documents
                  </span>
                ),
                children: (
                  <Card>
                    <div style={{ marginBottom: 16 }}>
                      <Space wrap>
                        <Select
                          style={{ width: 220 }}
                          value={uploadDocType}
                          onChange={setUploadDocType}
                          options={STAFF_DOCUMENT_TYPES}
                          placeholder="Document Type"
                        />
                        <DatePicker
                          placeholder="Expiry Date (optional)"
                          value={uploadExpiry}
                          onChange={setUploadExpiry}
                          style={{ width: 180 }}
                        />
                        <Upload
                          beforeUpload={(file) => {
                            handleUpload(file);
                            return false;
                          }}
                          showUploadList={false}
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        >
                          <Button
                            icon={<UploadOutlined />}
                            loading={uploading}
                            type="primary"
                          >
                            Upload Document
                          </Button>
                        </Upload>
                      </Space>
                    </div>

                    <Spin spinning={loadingFiles}>
                      {filesData?.items && filesData.items.length > 0 ? (
                        <Table
                          dataSource={filesData.items}
                          rowKey="id"
                          pagination={false}
                          size="small"
                          columns={[
                            {
                              title: "Document",
                              dataIndex: "fileName",
                              key: "fileName",
                              render: (text, record) => (
                                <Space>
                                  <FileOutlined />
                                  <span>{text || record.title}</span>
                                </Space>
                              ),
                            },
                            {
                              title: "Type",
                              dataIndex: "type",
                              key: "type",
                              render: (type) => (
                                <Tag>{getDocumentTypeLabel(type)}</Tag>
                              ),
                            },
                            {
                              title: "Expires",
                              dataIndex: "expires",
                              key: "expires",
                              render: (expires) =>
                                expires ? (
                                  <Tag
                                    color={
                                      dayjs(expires).isBefore(dayjs())
                                        ? "red"
                                        : dayjs(expires).isBefore(
                                            dayjs().add(30, "day")
                                          )
                                        ? "orange"
                                        : "green"
                                    }
                                  >
                                    {dayjs(expires).format("MMM D, YYYY")}
                                  </Tag>
                                ) : (
                                  <span style={{ color: "#999" }}>-</span>
                                ),
                            },
                            {
                              title: "Uploaded",
                              dataIndex: "createdAt",
                              key: "createdAt",
                              render: (date) =>
                                dayjs(date).format("MMM D, YYYY"),
                            },
                            {
                              title: "Actions",
                              key: "actions",
                              render: (_, record) => (
                                <Space>
                                  <Button
                                    type="text"
                                    icon={<EyeOutlined />}
                                    size="small"
                                    title="View/Download"
                                    onClick={() =>
                                      window.open(record.filePath, "_blank")
                                    }
                                  />
                                  <Button
                                    type="text"
                                    icon={<EditOutlined />}
                                    size="small"
                                    title="Edit"
                                    onClick={() => handleEditFile(record)}
                                  />
                                  <Popconfirm
                                    title="Delete this document?"
                                    onConfirm={() => handleDelete(record.id)}
                                    okText="Yes"
                                    cancelText="No"
                                  >
                                    <Button
                                      type="text"
                                      danger
                                      icon={<DeleteOutlined />}
                                      size="small"
                                      title="Delete"
                                    />
                                  </Popconfirm>
                                </Space>
                              ),
                            },
                          ]}
                        />
                      ) : (
                        <Empty
                          description="No documents uploaded"
                          style={{ padding: "40px 0" }}
                        />
                      )}
                    </Spin>
                  </Card>
                ),
              },
            ]}
          />
        ) : (
          <Empty description="Staff not found" />
        )}
      </Spin>

      {/* Edit Document Modal */}
      <Modal
        title="Edit Document"
        open={!!editingFile}
        onCancel={() => {
          setEditingFile(null);
          editForm.resetFields();
        }}
        onOk={handleUpdateFile}
        okText="Save"
        confirmLoading={updateFileMutation.isPending}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="type"
            label="Document Type"
            rules={[{ required: true, message: "Please select a document type" }]}
          >
            <Select
              options={STAFF_DOCUMENT_TYPES}
              placeholder="Select document type"
            />
          </Form.Item>
          <Form.Item name="expires" label="Expiry Date">
            <DatePicker style={{ width: "100%" }} placeholder="Select expiry date (optional)" />
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  );
};
